import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdCloudDone, MdDelete, MdDevices, MdEdit, MdRefresh, MdWarningAmber } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import StatsGrid from '../../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../../shared/layout/cards/StatCard';
import {
    fetchManufacturerIntegrations,
    fetchManufacturerIntegration,
    createManufacturerIntegration,
    updateManufacturerIntegration,
    deleteManufacturerIntegration,
    rotateManufacturerIntegrationCredentials,
    pingManufacturerIntegrationWebhook,
} from '../../../../services/Http/ManufacturerApi';

const defaultForm = {
    id: null,
    name: '',
    integration_type: 'custom',
    email: '',
    webhook_url: '',
    config: {
        endpoint: '',
        sync_interval: 'daily',
    },
    is_active: true,
};

const ManufacturerIntegrations = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [connectors, setConnectors] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, webhook_alerts: 0, types: {} });
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [lastSecret, setLastSecret] = useState(null);

    const loadIntegrations = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const response = await fetchManufacturerIntegrations();
            const rows = response.data || [];
            setConnectors(rows);
            setStats(response.stats || { total: 0, active: 0, webhook_alerts: 0, types: {} });
            setSelectedId((prev) => {
                if (prev && rows.some((row) => row.id === prev)) {
                    return prev;
                }
                return rows[0]?.id || null;
            });
        } catch (err) {
            console.error(err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadIntegrations();
    }, [loadIntegrations]);

    const loadDetail = useCallback(async (integrationId) => {
        if (!integrationId) {
            setDetail(null);
            return;
        }
        setDetailLoading(true);
        try {
            const response = await fetchManufacturerIntegration(integrationId);
            setDetail(response);
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerIntegrations.toast.error'));
        } finally {
            setDetailLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadDetail(selectedId);
    }, [selectedId, loadDetail]);

    const openModal = (connector = null) => {
        if (connector) {
            setForm({
                id: connector.id,
                name: connector.name,
                integration_type: connector.integration_type,
                email: connector.email || '',
                webhook_url: connector.webhook_url || '',
                config: {
                    endpoint: connector.config?.endpoint || '',
                    sync_interval: connector.config?.sync_interval || 'daily',
                },
                is_active: connector.is_active,
            });
        } else {
            setForm({ ...defaultForm, config: { ...defaultForm.config } });
        }
        setModalOpen(true);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            if (form.id) {
                await updateManufacturerIntegration(form.id, form);
                toast.success(t('ManufacturerIntegrations.toast.updated'));
            } else {
                const response = await createManufacturerIntegration(form);
                if (response?.api_secret) {
                    setLastSecret(response.api_secret);
                    toast.info(t('ManufacturerIntegrations.toast.secret'));
                }
                toast.success(t('ManufacturerIntegrations.toast.created'));
            }
            setModalOpen(false);
            setForm({ ...defaultForm, config: { ...defaultForm.config } });
            await loadIntegrations();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerIntegrations.toast.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (connector) => {
        if (!window.confirm(t('ManufacturerIntegrations.actions.confirmDelete'))) {
            return;
        }
        try {
            await deleteManufacturerIntegration(connector.id);
            toast.success(t('ManufacturerIntegrations.toast.deleted'));
            if (connector.id === selectedId) {
                setSelectedId(null);
            }
            await loadIntegrations();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerIntegrations.toast.error'));
        }
    };

    const handleRotate = async (connector) => {
        try {
            const response = await rotateManufacturerIntegrationCredentials(connector.id);
            setLastSecret(response.api_secret);
            toast.success(t('ManufacturerIntegrations.toast.token'));
            await loadIntegrations();
            await loadDetail(connector.id);
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerIntegrations.toast.error'));
        }
    };

    const handleWebhookTest = async (connector) => {
        try {
            await pingManufacturerIntegrationWebhook(connector.id);
            toast.success(t('ManufacturerIntegrations.toast.webhook'));
            await loadIntegrations();
            await loadDetail(connector.id);
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerIntegrations.toast.error'));
        }
    };

    const typeBreakdown = useMemo(() => {
        const entries = Object.entries(stats.types || {});
        if (!entries.length) {
            return null;
        }
        return entries.map(([type, count]) => `${type}: ${count}`).join(' · ');
    }, [stats.types]);

    if (loading) {
        return <LoadingMessage>{t('ManufacturerIntegrations.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('ManufacturerIntegrations.error')}</ErrorMessage>;
    }

    const activeConnector = connectors.find((row) => row.id === selectedId) || null;

    return (
        <PageContainer>
            <PageHeader>
                <TitleGroup>
                    <PageTitle>{t('ManufacturerIntegrations.title')}</PageTitle>
                    <Subtitle>{t('ManufacturerIntegrations.subtitle')}</Subtitle>
                </TitleGroup>
                <PrimaryButton type="button" onClick={() => openModal(null)}>
                    <MdAdd /> {t('ManufacturerIntegrations.actions.add')}
                </PrimaryButton>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    <StatCard
                        icon={<MdDevices className="icon" />}
                        title={t('ManufacturerIntegrations.stats.total')}
                        value={stats.total}
                        color="venues"
                    />
                    <StatCard
                        icon={<MdCloudDone className="icon" />}
                        title={t('ManufacturerIntegrations.stats.active')}
                        value={stats.active}
                        color="users"
                    />
                    <StatCard
                        icon={<MdWarningAmber className="icon" />}
                        title={t('ManufacturerIntegrations.stats.webhook')}
                        value={stats.webhook_alerts}
                        color="transactions"
                    />
                </StatsGrid>

                {typeBreakdown && <Breakdown>{typeBreakdown}</Breakdown>}

                <ConnectorsTable>
                    <thead>
                        <tr>
                            <th>{t('ManufacturerIntegrations.table.name')}</th>
                            <th>{t('ManufacturerIntegrations.table.type')}</th>
                            <th>{t('ManufacturerIntegrations.table.status')}</th>
                            <th>{t('ManufacturerIntegrations.table.lastSync')}</th>
                            <th>{t('ManufacturerIntegrations.table.webhook')}</th>
                            <th>{t('ManufacturerIntegrations.table.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {connectors.map((connector) => (
                            <tr
                                key={connector.id}
                                className={selectedId === connector.id ? 'active' : ''}
                                onClick={() => setSelectedId(connector.id)}
                            >
                                <td>{connector.name}</td>
                                <td>{connector.integration_type}</td>
                                <td>
                                    <StatusBadge $variant={connector.status}>
                                        {t(`ManufacturerIntegrations.status.${connector.status}`)}
                                    </StatusBadge>
                                </td>
                                <td>{connector.last_sync_at || t('ManufacturerIntegrations.table.never')}</td>
                                <td>
                                    <StatusBadge $variant={connector.webhook_health}>
                                        {t(`ManufacturerIntegrations.webhook.${connector.webhook_health}`)}
                                    </StatusBadge>
                                </td>
                                <td>
                                    <RowActions>
                                        <IconButton type="button" onClick={() => openModal(connector)}>
                                            <MdEdit />
                                        </IconButton>
                                        <IconButton type="button" onClick={() => handleRotate(connector)}>
                                            <MdRefresh />
                                        </IconButton>
                                        <IconButton type="button" onClick={() => handleDelete(connector)}>
                                            <MdDelete />
                                        </IconButton>
                                    </RowActions>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </ConnectorsTable>

                {activeConnector && (
                    <DetailPanel>
                        <h3>{activeConnector.name}</h3>
                        {detailLoading && <p>{t('ManufacturerIntegrations.loading')}</p>}
                        <DetailGrid>
                            <div>
                                <Label>{t('ManufacturerIntegrations.detail.type')}</Label>
                                <p>{detail?.integration_type || activeConnector.integration_type}</p>
                            </div>
                            <div>
                                <Label>{t('ManufacturerIntegrations.detail.endpoint')}</Label>
                                <p>{detail?.config?.endpoint || activeConnector.config?.endpoint || '—'}</p>
                            </div>
                            <div>
                                <Label>{t('ManufacturerIntegrations.detail.sync')}</Label>
                                <p>{detail?.config?.sync_interval || activeConnector.config?.sync_interval || '—'}</p>
                            </div>
                            <div>
                                <Label>{t('ManufacturerIntegrations.detail.apiKey')}</Label>
                                <code>{detail?.api_key || activeConnector.api_key}</code>
                            </div>
                            {lastSecret && (
                                <div>
                                    <Label>{t('ManufacturerIntegrations.detail.apiSecret')}</Label>
                                    <SecretBox>{lastSecret}</SecretBox>
                                </div>
                            )}
                            <div>
                                <Label>{t('ManufacturerIntegrations.detail.webhookUrl')}</Label>
                                <p>{detail?.webhook_url || activeConnector.webhook_url || '—'}</p>
                            </div>
                            <div>
                                <Label>{t('ManufacturerIntegrations.detail.webhookSecret')}</Label>
                                <code>{detail?.webhook_secret || '—'}</code>
                            </div>
                        </DetailGrid>
                        <SecondaryButton type="button" onClick={() => handleWebhookTest(activeConnector)}>
                            {t('ManufacturerIntegrations.actions.testWebhook')}
                        </SecondaryButton>
                    </DetailPanel>
                )}
            </PageContent>

            {modalOpen && (
                <ModalOverlay>
                    <ModalContent>
                        <ModalHeader>
                            <h3>{form.id ? t('ManufacturerIntegrations.editTitle') : t('ManufacturerIntegrations.addTitle')}</h3>
                            <button type="button" onClick={() => setModalOpen(false)}>×</button>
                        </ModalHeader>
                        <form onSubmit={handleSubmit}>
                            <ModalBody>
                                <label>{t('ManufacturerIntegrations.form.name')}</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                                    required
                                />
                                <label>{t('ManufacturerIntegrations.form.type')}</label>
                                <select
                                    value={form.integration_type}
                                    onChange={(event) => setForm((prev) => ({ ...prev, integration_type: event.target.value }))}
                                >
                                    <option value="erp">ERP</option>
                                    <option value="pos">POS</option>
                                    <option value="crm">CRM</option>
                                    <option value="custom">{t('ManufacturerIntegrations.form.custom')}</option>
                                </select>
                                <label>{t('ManufacturerIntegrations.form.email')}</label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                                />
                                <label>{t('ManufacturerIntegrations.form.endpoint')}</label>
                                <input
                                    type="url"
                                    value={form.config.endpoint}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            config: { ...prev.config, endpoint: event.target.value },
                                        }))
                                    }
                                />
                                <label>{t('ManufacturerIntegrations.form.sync')}</label>
                                <select
                                    value={form.config.sync_interval}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            config: { ...prev.config, sync_interval: event.target.value },
                                        }))
                                    }
                                >
                                    <option value="realtime">{t('ManufacturerIntegrations.form.syncRealtime')}</option>
                                    <option value="hourly">{t('ManufacturerIntegrations.form.syncHourly')}</option>
                                    <option value="daily">{t('ManufacturerIntegrations.form.syncDaily')}</option>
                                </select>
                                <label>{t('ManufacturerIntegrations.form.webhook')}</label>
                                <input
                                    type="url"
                                    value={form.webhook_url}
                                    onChange={(event) => setForm((prev) => ({ ...prev, webhook_url: event.target.value }))}
                                />
                                <ToggleRow>
                                    <input
                                        type="checkbox"
                                        checked={form.is_active}
                                        onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                                    />
                                    <span>{t('ManufacturerIntegrations.form.isActive')}</span>
                                </ToggleRow>
                            </ModalBody>
                            <ModalFooter>
                                <SecondaryButton type="button" onClick={() => setModalOpen(false)}>
                                    {t('ManufacturerIntegrations.actions.cancel')}
                                </SecondaryButton>
                                <PrimaryButton as="button" type="submit" disabled={saving}>
                                    {saving ? t('ManufacturerIntegrations.actions.saving') : t('ManufacturerIntegrations.actions.save')}
                                </PrimaryButton>
                            </ModalFooter>
                        </form>
                    </ModalContent>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

export default ManufacturerIntegrations;

const TitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    border: none;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border-radius: 8px;
    padding: 0.625rem 1rem;
    cursor: pointer;
`;

const SecondaryButton = styled.button`
    border: 1px solid rgba(130, 22, 146, 0.4);
    color: rgba(130, 22, 146, 1);
    background: transparent;
    border-radius: 8px;
    padding: 0.625rem 1rem;
    cursor: pointer;
`;

const Breakdown = styled.p`
    margin: 0.75rem 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const ConnectorsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;

    th,
    td {
        padding: 0.75rem;
        border-bottom: 1px solid rgba(130, 22, 146, 0.1);
    }

    tr.active {
        background: rgba(130, 22, 146, 0.05);
    }
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 999px;
    font-size: 0.875rem;
    text-transform: capitalize;
    background: ${({ $variant }) => {
        switch ($variant) {
            case 'healthy':
                return 'rgba(76, 175, 80, 0.15)';
            case 'warning':
            case 'stale':
                return 'rgba(255, 152, 0, 0.15)';
            case 'failing':
            case 'inactive':
                return 'rgba(244, 67, 54, 0.15)';
            default:
                return 'rgba(158, 158, 158, 0.15)';
        }
    }};
`;

const RowActions = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    border: none;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    border-radius: 6px;
    padding: 0.375rem;
    cursor: pointer;
`;

const DetailPanel = styled.div`
    margin-top: 1.5rem;
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1.25rem;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
`;

const Label = styled.span`
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const SecretBox = styled.code`
    display: block;
    padding: 0.625rem;
    background: rgba(130, 22, 146, 0.08);
    border-radius: 8px;
    word-break: break-all;
`;

const ToggleRow = styled.label`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-top: 0.5rem;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: #fff;
    border-radius: 12px;
    width: min(520px, 95%);
    max-height: 90vh;
    overflow: auto;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid rgba(130, 22, 146, 0.1);

    button {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
    }
`;

const ModalBody = styled.div`
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    input,
    select {
        border-radius: 8px;
        border: 1px solid rgba(130, 22, 146, 0.2);
        padding: 0.625rem;
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem;
    border-top: 1px solid rgba(130, 22, 146, 0.1);
`;
